import { useState } from 'react';
import type { BlockProgram } from '../behavior/types';
import type { ProjectKind } from '../data/types';
import { useProjectsStoreApi } from '../stores/projectsContext';
import { useCurrentUser } from './useAuth';
import { useCreateServerProject } from './useServerProjects';
import './SyncButton.css';

/**
 * Header button that snapshots the current local project to the
 * signed-in kid's account. The button only renders when signed in —
 * nothing nags a local-first kid to make an account.
 *
 * Phase 3 v1 behaviour: every click creates a NEW server-side
 * project. We don't yet track "this local project has a synced
 * counterpart" — that arrives with the gallery publish flow when we
 * need real two-way state. For now the button is a one-shot snapshot
 * of name + kind + block program.
 */

export interface SyncButtonProps {
  /** The local project's id — used to bind the server-side mirror's
   *  id back via `setServerId` so subsequent clicks short-circuit
   *  instead of creating a duplicate. */
  projectId: string;
  projectName: string;
  projectKind: ProjectKind;
  blockProgram?: BlockProgram | undefined;
  /** When set, this project is already mirrored to the server — the
   *  button shows "Synced ✓" and is disabled until the kid makes
   *  more changes. v1 doesn't push diffs back; future iterations
   *  will. NULL for never-synced projects. */
  serverId?: string | null;
}

type Status = 'idle' | 'syncing' | 'synced' | 'error';

export function SyncButton({
  projectId,
  projectName,
  projectKind,
  blockProgram,
  serverId,
}: SyncButtonProps): JSX.Element | null {
  const { data: user } = useCurrentUser();
  const create = useCreateServerProject();
  const store = useProjectsStoreApi();
  // If the kid arrived at this project via a classroom-banner Start
  // (or already synced once), the project already has a server-side
  // mirror. Show "Synced ✓" and short-circuit the click. Without this
  // every visit could spawn another server-side row.
  const [status, setStatus] = useState<Status>(() => (serverId ? 'synced' : 'idle'));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!user) return null;

  const handleClick = (): void => {
    if (status === 'syncing' || serverId) return;
    setStatus('syncing');
    setErrorMessage(null);
    create.mutate(
      {
        name: projectName,
        kind: projectKind,
        block_program: blockProgram ?? null,
      },
      {
        onSuccess: async (created) => {
          // Bind the local project to its server twin so the next
          // click + any other surface that needs the mapping (e.g.
          // the publish flow) knows about it.
          await store.getState().setServerId(projectId, created.id);
          setStatus('synced');
        },
        onError: (err) => {
          setStatus('error');
          setErrorMessage(err.message);
        },
      },
    );
  };

  const label =
    status === 'syncing' ? 'Syncing…' : status === 'synced' ? 'Synced ✓' : 'Sync to my account';

  return (
    <span className="bl-sync">
      <button
        type="button"
        className={`bl-sync__btn bl-sync__btn--${status}`}
        onClick={handleClick}
        disabled={status === 'syncing' || status === 'synced'}
        title={status === 'synced' ? 'This project is already in your account.' : undefined}
      >
        {label}
      </button>
      {status === 'error' && errorMessage && (
        <span role="alert" className="bl-sync__error">
          {errorMessage}
        </span>
      )}
    </span>
  );
}
